import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { getMallList } from 'app/api/mall';
import {
  addFestiveTier,
  getFestiveTierById,
  getDefaultTierByMallId,
  updateFestiveTier,
} from 'app/api/tier';
import { APP_CONSTANTS } from 'app/constants/app-constants';
import {
  festivetierValidationRules,
  validate,
} from 'app/constants/validation-constants';
import Message from 'app/utils/Message';
import { dataformatChangesYearToDate } from 'app/utils/Parsers';
import Toast from 'app/utils/Toast';

const useCreateFestiveTier = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pageLoading, setPageLoading] = useState(false);
  const [mallList, setMallList] = useState([]);
  const [masterList, setMasterList] = useState([]);
  const [error, setErrors] = useState({});
  const [values, setValues] = useState({
    tierType: 'festive',
    tierDescription: 'festive',
    parent: '',
    mallId: '',
    status: true,
    name: '',
    discount: '',
    discountType: 'percentage',
    startDate: null,
    endDate: null,
  });
  const [dayOfweek, setWeekDays] = useState({
    Mon: false,
    Tue: false,
    Wed: false,
    Thu: false,
    Fri: false,
    Sat: false,
    Sun: false,
  });
  const toggleDayStatus = (day) => {
    setWeekDays((prevState) => ({
      ...prevState,
      [day]: !prevState[day],
    }));
  };

  const supportId = location?.state?.id ? location?.state?.id : '';

  useEffect(() => {
    getMallListforDropdown();

    if (supportId) {
      loadStoreData(supportId);
    }
  }, []);

  async function getMallListforDropdown() {
    setPageLoading(true);
    let response = await getMallList(1);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.malls?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.name,
        };
      });
      setMallList(temp);
    } else {
      Toast.error(
        response?.data ? response?.data : Message?.Error?.COMMON_MESSAGE
      );
    }
  }

  async function getMasterList(id, baseTierId) {
    setPageLoading(true);
    let response = await getDefaultTierByMallId(id);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.map((item, index) => {
        if (baseTierId && baseTierId === item?.id) {
          return {
            ...item,
            value: item?.id,
            label: item.name,
            checked: true,
          };
        }
        return {
          ...item,
          value: item.id,
          label: item.name,
          checked: false,
        };
      });
      setMasterList(temp);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  }

  async function loadStoreData(id) {
    if (!id) {
      return;
    }
    setPageLoading(true);
    let response = await getFestiveTierById(id, `${APP_CONSTANTS.ADD0N}`);
    setPageLoading(false);
    if (response?.responseCode === 200) {
      let responsedata = response?.data;
      setValues({
        discount: responsedata?.discount,
        name: responsedata?.name,
        mallId: responsedata?.mallId,
        status: responsedata?.status,
        startDate: dataformatChangesYearToDate(responsedata?.startDate),
        endDate: dataformatChangesYearToDate(responsedata?.endDate),
      });
      getMasterList(responsedata?.mallId, responsedata?.parent);
      handleCheckChange({
        target: { id: responsedata?.parent, checked: true },
      });
      const dayOfWeekMap = Array.isArray(responsedata?.dayOfweek)
        ? responsedata.dayOfweek.reduce((acc, day) => {
            acc[day.day] = day.status;
            return acc;
          }, {})
        : {};

      setWeekDays({
        Mon: dayOfWeekMap?.Mon || false,
        Tue: dayOfWeekMap?.Tue || false,
        Wed: dayOfWeekMap?.Wed || false,
        Thu: dayOfWeekMap?.Thu || false,
        Fri: dayOfWeekMap?.Fri || false,
        Sat: dayOfWeekMap?.Sat || false,
        Sun: dayOfWeekMap?.Sun || false,
      });
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  }

  async function callAddOrUpdateAPI() {
    setPageLoading(true);
    let response = {};

    let senddata = {
      ...values,
      parent: parseInt(values.parent),
      dayOfweek: dayOfweek,
    };

    if (supportId) {
      response = await updateFestiveTier(supportId, { ...senddata });
    } else {
      response = await addFestiveTier({ ...senddata });
    }
    setPageLoading(false);

    if (response?.responseCode === 200) {
      Toast.success(
        supportId
          ? Message.FestiveTier.UPDATED_SUCCESS
          : Message.FestiveTier.CREATED_SUCCESS
      );
      navigate(-1);
    } else {
      Toast.error(
        response?.message ? response?.message : Message.Error.COMMON_MESSAGE
      );
    }
  }

  function handleCreateFestiveTier(e) {
    e.preventDefault();
    if (masterList?.length === 0 && error?.parent) {
      Toast.error('Default Tier is Required');
    }
    const validationErrors = validate(values, festivetierValidationRules);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      callAddOrUpdateAPI();
    }
  }
  function handleChange(e) {
    if (e && e.target) {
      const { name, value } = e.target;
      setValues({
        ...values,
        [name]: name === 'discount' ? parseFloat(value) : value,
      });

      if (error[name]) {
        setErrors({
          ...error,
          [name]: '',
        });
      }
    }
  }
  function setChange(name, val) {
    setValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
    setErrors({
      ...error,
      [name]: '',
    });
  }

  function handleCheckChange(e) {
    const { id, checked } = e.target;
    setErrors({
      ...error,
      parent: '',
    });
    if (!checked) {
      const updatedMasterList = masterList.map((item) => ({
        ...item,
        checked: false,
      }));
      setMasterList(updatedMasterList);
      setValues((prevValues) => ({
        ...prevValues,
        parent: '',
      }));
      return;
    }

    const updatedMasterList = masterList.map((item) => ({
      ...item,
      checked: item.id.toString() === id ? true : false,
    }));
    setValues((prevValues) => ({
      ...prevValues,
      parent: id,
    }));
    setMasterList([...updatedMasterList]);
  }
  function handleMallChange(e) {
    if (e && e.target) {
      const { name, value } = e.target;
      setMasterList([]);

      getMasterList(value);

      setValues({
        ...values,
        [name]: value,
        parent: '',
      });

      if (error[name]) {
        setErrors({
          ...error,
          [name]: '',
          parent: '',
        });
      }
    }
  }
  return {
    handleMallChange,
    setChange,
    handleChange,
    values,
    error,
    handleCreateFestiveTier,
    pageLoading,
    supportId,
    mallList,
    masterList,
    navigate,
    handleCheckChange,
    toggleDayStatus,
    dayOfweek,
  };
};

export default useCreateFestiveTier;
